import { axonAdapterGreengrassComponentName } from './greengrass-component-names.js';
import { GatewayAdapterKeySchema, toThingGroupSuffix } from './gateway-adapter-common.js';
export const EfemAdapterKeySchema = GatewayAdapterKeySchema;
export { toThingGroupSuffix };
const DEFAULT_ORCHESTRATOR = {
    efemAdapterUrl: 'http://127.0.0.1:8088/api',
    efemAdapterTimeout: 30000,
    attestationGuardBaseUrl: 'http://127.0.0.1:8791',
    attestationGuardTimeoutMs: 1500,
    heartbeatIntervalMs: 60000,
    features: { barcodeScanner: true, waferidCamera: true },
};
const DEFAULT_BARCODE = {
    profiles: [{ usbVendorId: '0x24EA', usbProductId: '0x0187' }],
    baudRate: 9600,
    delimiter: '\r',
    reconnectIntervalMs: 5000,
    debounceMs: 2000,
    discoveryPollIntervalMs: 30000,
    commandScanTimeoutMs: 10000,
};
/** Rakinda S2 handheld in USB VCP mode (WCH CH340 bridge — e.g. COM11 on Windows). */
const DEFAULT_HANDHELD_BARCODE = {
    profiles: [
        {
            usbVendorId: '0x1A86',
            usbProductId: '0x7523',
            identity: { mode: 'cloud-resolve' },
        },
    ],
    baudRate: 9600,
    delimiter: '\r',
    reconnectIntervalMs: 5000,
    debounceMs: 2000,
    discoveryPollIntervalMs: 30000,
    commandScanTimeoutMs: 10000,
};
const DEFAULT_CAMERA = {
    profiles: [{ host: '192.168.30.20', port: 23 }],
    username: 'admin',
    password: '',
    timeout: 10000,
    simulatorMode: false,
    discoveryPollIntervalMs: 30000,
    reconnectIntervalMs: 5000,
    captureProtocol: 'telnet',
    insightPort: 1069,
    jobFilename: 'WafID450.job',
    initialLighting: { mode: 1, power: 2 },
    autoAdjust: { targetGray: 225, tolerance: 20, maxIterations: 10 },
    imageQualityOptions: {
        saturationThreshold: 240,
        saturationPercentageLimit: 20,
        darkPercentile: 0.99,
        darkGrayLimit: 20,
        blankImageTolerance: 3,
    },
};
function entry(partial) {
    const efemAdapterKey = EfemAdapterKeySchema.parse(partial.efemAdapterKey);
    return {
        ...partial,
        efemAdapterKey,
        thingGroupSuffix: toThingGroupSuffix(efemAdapterKey),
        defaultOrchestratorUrl: partial.defaultOrchestratorUrl ?? 'http://127.0.0.1:8088/api',
        defaultRestListenPort: partial.defaultRestListenPort ?? 8088,
    };
}
/** Code registry — new OEM adapters require a new entry + Greengrass recipe (v1). */
export const EFEM_ADAPTER_REGISTRY = [
    entry({
        efemAdapterKey: 'efem-csv-stub-adapter-1',
        displayName: 'EFEM CSV Stub Adapter 1 (test)',
        greengrassComponentName: axonAdapterGreengrassComponentName('efem-csv-stub-adapter-1'),
        defaultConnectionTemplate: { dataDirectory: 'C:\\Cnapse\\efem-csv-stub-adapter-1\\data' },
        isDevSimulator: true,
        windowsServiceName: 'CnapseEfemCsvStubAdapter1',
        needsAppConfigXml: false,
        systemTemplate: {
            orchestrator: { ...DEFAULT_ORCHESTRATOR, efemAdapterUrl: 'http://127.0.0.1:8088/api' },
            barcodeScanner: { ...DEFAULT_BARCODE },
            handheldBarcodeScanner: { ...DEFAULT_HANDHELD_BARCODE },
            waferidCamera: {
                ...DEFAULT_CAMERA,
            },
            efemAdapter: {
                efemAdapterKey: 'efem-csv-stub-adapter-1',
                adapterDisplayName: 'EFEM CSV Stub Adapter 1',
                connection: { dataDirectory: 'C:\\Cnapse\\efem-csv-stub-adapter-1\\data' },
                rest: { listenHost: '0.0.0.0', listenPort: 8088, basePath: '/api' },
                timeouts: { operationMs: 30000, healthMs: 5000 },
            },
        },
    }),
    entry({
        efemAdapterKey: 'efem-csv-stub-adapter-2',
        displayName: 'EFEM CSV Stub Adapter 2 (test)',
        greengrassComponentName: axonAdapterGreengrassComponentName('efem-csv-stub-adapter-2'),
        defaultConnectionTemplate: { dataDirectory: 'C:\\Cnapse\\efem-csv-stub-adapter-2\\data' },
        isDevSimulator: true,
        windowsServiceName: '   ',
        needsAppConfigXml: false,
        systemTemplate: {
            orchestrator: { ...DEFAULT_ORCHESTRATOR, efemAdapterUrl: 'http://127.0.0.1:8088/api' },
            barcodeScanner: { ...DEFAULT_BARCODE },
            handheldBarcodeScanner: { ...DEFAULT_HANDHELD_BARCODE },
            waferidCamera: {
                ...DEFAULT_CAMERA,
            },
            efemAdapter: {
                efemAdapterKey: 'efem-csv-stub-adapter-2',
                adapterDisplayName: 'EFEM CSV Stub Adapter 2',
                connection: { dataDirectory: 'C:\\Cnapse\\efem-csv-stub-adapter-2\\data' },
                rest: { listenHost: '0.0.0.0', listenPort: 8088, basePath: '/api' },
                timeouts: { operationMs: 30000, healthMs: 5000 },
            },
        },
    }),
];
const REGISTRY_BY_KEY = new Map(EFEM_ADAPTER_REGISTRY.map((e) => [e.efemAdapterKey, e]));
export function getEfemAdapterRegistryEntry(efemAdapterKey) {
    const key = EfemAdapterKeySchema.parse(efemAdapterKey);
    const found = REGISTRY_BY_KEY.get(key);
    if (!found) {
        throw new Error(`Unknown efemAdapterKey: ${key}`);
    }
    return found;
}
export function listEfemAdapterRegistryEntries() {
    return [...EFEM_ADAPTER_REGISTRY];
}
/** Windows service to restart on config apply, or null when Greengrass owns the adapter process. */
export function getEfemAdapterWindowsServiceName(efemAdapterKey) {
    try {
        const name = getEfemAdapterRegistryEntry(efemAdapterKey).windowsServiceName?.trim();
        return name || null;
    }
    catch {
        return null;
    }
}
/** Map legacy push UI values to registry keys until all gateways are recommissioned. */
export function resolveEfemAdapterKey(input) {
    const normalized = EfemAdapterKeySchema.parse(input);
    if (REGISTRY_BY_KEY.has(normalized))
        return normalized;
    const legacyMap = {
        'dotnet-remoting': 'dev-dotnet-remoting',
        'rest-http': 'dev-rest-http',
        'csv-simulator': 'csv-simulator',
    };
    const mapped = legacyMap[normalized];
    if (mapped)
        return mapped;
    throw new Error(`Unknown efemAdapterKey: ${input}`);
}
//# sourceMappingURL=efem-adapter-registry.js.map